import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useSelection } from './SelectionContext';

const SankeyContext = createContext(null);

export const SankeyProvider = ({ children }) => {
  // Get map selection state for synchronization
  const {
    view1Origin,
    view1Destination,
    view1DayType,
    view1TimeSlot,
    setView1Origin,
    setView1Destination,
    setView1DayType,
    setView1TimeSlot,
  } = useSelection();

  // View state
  const [isOpen, setIsOpen] = useState(false);

  // Sankey-specific filters (not shared with map)
  const [minFlowLimit, setMinFlowLimit] = useState(1);

  // Use map's day type and time slot (synchronized)
  const dayType = view1DayType;
  const timeSlot = view1TimeSlot;
  const setDayType = setView1DayType;
  const setTimeSlot = setView1TimeSlot;

  // Selected origins/destinations for Sankey (synchronized with map origin)
  // When map has an origin selected, use it as the filter
  const [selectedOrigins, setSelectedOriginsInternal] = useState([]);
  const [selectedDestinations, setSelectedDestinationsInternal] = useState([]);

  // Sync Sankey origins with map origin
  useEffect(() => {
    if (view1Origin) {
      setSelectedOriginsInternal([view1Origin]);
    } else {
      setSelectedOriginsInternal([]);
    }
  }, [view1Origin]);

  // Sync Sankey destinations with map destination
  useEffect(() => {
    if (view1Destination) {
      setSelectedDestinationsInternal([view1Destination]);
    } else {
      setSelectedDestinationsInternal([]);
    }
  }, [view1Destination]);

  // Custom setters that sync back to map
  const setSelectedOrigins = useCallback((origins) => {
    setSelectedOriginsInternal(origins);
    // Sync to map - use first origin if array has items
    if (Array.isArray(origins) && origins.length > 0) {
      setView1Origin(origins[0]);
    } else if (Array.isArray(origins) && origins.length === 0) {
      setView1Origin(null);
    }
  }, [setView1Origin]);

  const setSelectedDestinations = useCallback((destinations) => {
    setSelectedDestinationsInternal(destinations);
    // Sync to map - use first destination if array has items
    if (Array.isArray(destinations) && destinations.length > 0) {
      setView1Destination(destinations[0]);
    } else if (Array.isArray(destinations) && destinations.length === 0) {
      setView1Destination(null);
    }
  }, [setView1Destination]);

  // Open/close handlers
  const openSankey = useCallback(() => setIsOpen(true), []);
  const closeSankey = useCallback(() => setIsOpen(false), []);
  const toggleSankey = useCallback(() => setIsOpen((prev) => !prev), []);

  // Reset filters
  const resetFilters = useCallback(() => {
    setSelectedOrigins([]);
    setSelectedDestinations([]);
    setMinFlowLimit(1);
  }, [setSelectedOrigins, setSelectedDestinations]);

  const value = {
    // View state
    isOpen,
    openSankey,
    closeSankey,
    toggleSankey,

    // Filter state (synchronized with map)
    dayType,
    setDayType,
    timeSlot,
    setTimeSlot,
    selectedOrigins,
    setSelectedOrigins,
    selectedDestinations,
    setSelectedDestinations,

    // Sankey-specific filters
    minFlowLimit,
    setMinFlowLimit,

    // Actions
    resetFilters,
  };

  return (
    <SankeyContext.Provider value={value}>{children}</SankeyContext.Provider>
  );
};

export const useSankey = () => {
  const context = useContext(SankeyContext);
  if (!context) {
    throw new Error('useSankey must be used within a SankeyProvider');
  }
  return context;
};
